'use client';

import {useLocale} from 'next-intl';
import type {Projet} from './TunisiaMap';

interface MapSidebarProps {
  projet: Projet | null;
  onClose: () => void;
}

const statutConfig: Record<string, {bg: string; text: string; label: string}> = {
  publie: {bg: 'bg-blue-100', text: 'text-blue-800', label: 'En collecte'},
  en_cours: {bg: 'bg-green-100', text: 'text-green-800', label: 'En cours'},
  en_collecte: {bg: 'bg-orange-100', text: 'text-orange-800', label: 'En collecte'},
  finance: {bg: 'bg-green-100', text: 'text-green-800', label: 'Finance'},
  termine: {bg: 'bg-gray-100', text: 'text-gray-600', label: 'Termine'},
  cloture: {bg: 'bg-gray-100', text: 'text-gray-600', label: 'Cloture'},
  urgent: {bg: 'bg-red-100', text: 'text-red-800', label: 'Urgent'},
};

const categorieIcons: Record<string, string> = {
  education: '📚', sante: '🏥', logement: '🏠', microcredit: '💰',
  urgence: '🆘', infrastructure: '🏗️', environnement: '🌿',
};

// Simulated data for demo (in production, fetch from API)
function getResponsable(gouvernorat: string) {
  const responsables: Record<string, {nom: string; tel: string; email: string}> = {
    'Tunis': {nom: 'M. Ahmed Ben Salah', tel: '+216 71 808 066', email: 'tunis@utss.org.tn'},
    'Sfax': {nom: 'Mme Fatma Bouazizi', tel: '+216 74 221 100', email: 'sfax@utss.org.tn'},
    'Sousse': {nom: 'M. Kamel Nasri', tel: '+216 73 225 300', email: 'sousse@utss.org.tn'},
    'Kairouan': {nom: 'Mme Leila Hamdi', tel: '+216 77 231 500', email: 'kairouan@utss.org.tn'},
  };
  return responsables[gouvernorat] || {nom: `Chef Union Regionale ${gouvernorat}`, tel: '+216 71 808 066', email: 'contact@utss.org.tn'};
}

function getImpacts(categorie: string): string[] {
  const impacts: Record<string, string[]> = {
    education: ['Amelioration du taux de scolarisation', 'Reduction de l\'abandon scolaire', 'Acces a l\'education prescolaire'],
    sante: ['Acces aux soins de proximite', 'Reduction de la mortalite infantile', 'Amelioration de l\'hygiene'],
    logement: ['Amelioration des conditions de vie', 'Lutte contre l\'habitat precaire', 'Dignite des personnes agees'],
    microcredit: ['Creation d\'emplois locaux', 'Autonomisation economique des femmes', 'Reduction du chomage des jeunes'],
    urgence: ['Aide immediate aux sinistres', 'Protection des populations vulnerables', 'Reconstruction rapide'],
    infrastructure: ['Desenclavement des zones rurales', 'Acces a l\'eau potable', 'Amelioration des infrastructures'],
    environnement: ['Protection des ressources naturelles', 'Sensibilisation ecologique', 'Agriculture durable'],
  };
  return impacts[categorie] || ['Impact social positif', 'Amelioration des conditions de vie'];
}

export default function MapSidebar({projet, onClose}: MapSidebarProps) {
  const locale = useLocale();
  if (!projet) return null;

  const pct = projet.budget_objectif > 0
    ? Math.round((projet.montant_collecte / projet.budget_objectif) * 100)
    : 0;
  const statut = statutConfig[projet.statut] || statutConfig.en_collecte;
  const responsable = getResponsable(projet.gouvernorat);
  const impacts = getImpacts(projet.categorie);
  const icon = categorieIcons[projet.categorie] || '📋';

  return (
    <div className="w-full h-full bg-white flex flex-col overflow-hidden">
      {/* Header with close */}
      <div className="bg-primary text-white p-4 shrink-0">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xl">{icon}</span>
              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${statut.bg} ${statut.text}`}>{statut.label}</span>
              {projet.statut === 'urgent' && <span className="text-[10px] bg-red-500 text-white px-2 py-0.5 rounded-full">URGENT</span>}
            </div>
            <h2 className="text-lg font-bold leading-tight">{projet.titre}</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/20 shrink-0 ms-2" aria-label="Fermer">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        {/* Location */}
        <div className="px-4 py-3 bg-surface border-b border-border">
          <div className="flex items-center gap-1.5 text-sm text-muted">
            <svg className="w-4 h-4 text-primary shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
            <span>{projet.gouvernorat} &gt; {projet.delegation} &gt; {projet.imada}</span>
          </div>
          <div className="text-xs text-muted mt-1">Catégorie: <span className="font-medium text-foreground capitalize">{projet.categorie}</span></div>
        </div>

        {/* Financement */}
        <div className="px-4 py-4 border-b border-border">
          <h3 className="text-sm font-bold text-foreground mb-2">Financement</h3>
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-muted">{projet.montant_collecte.toLocaleString()} TND collectes</span>
            <span className="font-bold text-primary">{pct}%</span>
          </div>
          <div className="h-2.5 bg-border rounded-full overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
            <div className={`h-full rounded-full transition-all ${pct >= 100 ? 'bg-green-500' : pct >= 50 ? 'bg-primary' : 'bg-orange-500'}`} style={{width: `${Math.min(pct, 100)}%`}} />
          </div>
          <p className="text-xs text-muted mt-1">Objectif: <span className="font-medium">{projet.budget_objectif.toLocaleString()} TND</span></p>
        </div>

        {/* Impacts */}
        <div className="px-4 py-4 border-b border-border">
          <h3 className="text-sm font-bold text-foreground mb-2">{projet.statut === 'cloture' || projet.statut === 'termine' ? 'Impacts realises' : 'Impacts attendus'}</h3>
          <ul className="space-y-1.5">
            {impacts.map((impact, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-muted">
                <svg className="w-4 h-4 text-primary shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                {impact}
              </li>
            ))}
          </ul>
        </div>

        {/* Jalons / Planning */}
        {projet.jalons && projet.jalons.length > 0 && (
          <div className="px-4 py-4 border-b border-border">
            <h3 className="text-sm font-bold text-foreground mb-2">Planning / Jalons</h3>
            <div className="space-y-2">
              {projet.jalons.map((j, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${j.statut === 'atteint' ? 'bg-green-500' : j.statut === 'en_cours' ? 'bg-orange-400' : 'bg-gray-300'}`}>
                    {j.statut === 'atteint' ? (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                    ) : (
                      <span className="w-2 h-2 rounded-full bg-white" />
                    )}
                  </div>
                  <span className={`text-sm ${j.statut === 'atteint' ? 'text-foreground line-through' : 'text-foreground'}`}>{j.titre}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Photos */}
        <div className="px-4 py-4 border-b border-border">
          <h3 className="text-sm font-bold text-foreground mb-2">
            {projet.statut === 'cloture' || projet.statut === 'termine' ? 'Photos de realisation' : 'Photos du projet'}
          </h3>
          {projet.photos && projet.photos.length > 0 ? (
            <div className="grid grid-cols-3 gap-1.5">
              {projet.photos.slice(0, 6).map((photo: string, i: number) => (
                <div key={i} className="aspect-square rounded-lg overflow-hidden bg-surface">
                  <img src={photo} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-1.5">
              {[1, 2, 3].map(i => (
                <div key={i} className="aspect-square bg-surface rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-muted/40" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                </div>
              ))}
            </div>
          )}
          <p className="text-[11px] text-muted mt-1.5 italic">
            {projet.statut === 'cloture' || projet.statut === 'termine'
              ? 'Photos du projet termine'
              : 'Photos disponibles apres demarrage du projet'}
          </p>
        </div>

        {/* Responsable UTSS */}
        <div className="px-4 py-4 border-b border-border">
          <h3 className="text-sm font-bold text-foreground mb-2">Responsable UTSS</h3>
          <div className="bg-surface rounded-lg p-3">
            <p className="text-sm font-medium text-foreground">{responsable.nom}</p>
            <p className="text-xs text-muted mt-0.5">Union Regionale {projet.gouvernorat}</p>
            <div className="mt-2 space-y-1">
              <a href={`tel:${responsable.tel}`} className="flex items-center gap-2 text-xs text-primary hover:underline">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                {responsable.tel}
              </a>
              <a href={`mailto:${responsable.email}`} className="flex items-center gap-2 text-xs text-primary hover:underline">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                {responsable.email}
              </a>
            </div>
          </div>
        </div>

        {/* Demande info */}
        <div className="px-4 py-4">
          <a
            href={`/${locale}/contact?sujet=Projet&ref=${projet.id}`}
            className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg border-2 border-primary text-primary text-sm font-medium hover:bg-primary/5 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            Demander des informations
          </a>
        </div>
      </div>

      {/* CTA footer — adapte selon statut */}
      <div className="p-4 border-t border-border bg-white shrink-0 space-y-2">
        {projet.statut === 'cloture' || projet.statut === 'termine' ? (
          <>
            {/* Projet accompli — bilan au lieu de financement */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <span className="font-bold text-green-700">Projet accompli</span>
              </div>
              <p className="text-xs text-green-600">{pct}% finance — {(projet as any).nb_beneficiaires || '—'} beneficiaires</p>
            </div>
            <a
              href={`/${locale}/projets/${projet.id}`}
              style={{color: '#ffffff', textDecorationColor: 'transparent'}}
              className="flex items-center justify-center gap-2 w-full py-3 rounded-lg bg-primary text-white font-semibold hover:bg-primary-light transition-colors no-underline"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
              Voir la fiche et les realisations
            </a>
          </>
        ) : (
          <>
            <a
              href={`/${locale}/don?projet_id=${projet.id}`}
              style={{color: '#ffffff', textDecorationColor: 'transparent'}}
              className="flex items-center justify-center gap-2 w-full py-3 rounded-lg bg-primary text-white font-semibold hover:bg-primary-light transition-colors no-underline"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
              Financer ce projet
            </a>
            <a
              href={`/${locale}/projets/${projet.id}`}
              style={{color: '#1B5E20'}}
              className="block text-center text-xs hover:underline"
            >
              Voir la fiche complete →
            </a>
          </>
        )}
      </div>
    </div>
  );
}
